import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {Checkbox, CircularProgress} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../Services/ServiceLocator";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {Button} from "@material-ui/core";
import WorkshopTemplate from "./WorkshopTemplate";
import QrScanner from "./QrScanner";
import BomCheckModal from "./BomCheckModal";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { v4 as uuid } from 'uuid';
import PrintIcon from '@material-ui/icons/Print';
import { AuthenticationService } from "../../Services/AuthenticationService";
import {getNumberInputDisplayValue, parseNumberInputValue} from '../../utils/numberInputUtils';
import { toUtcForStorage } from '../../utils/dateUtils';

let assemblies = [];
let subAssemblies = [];
let packageLogs = [];
let assemblyFamilies = [];

const PACKAGE_FAMILY_ADJUSTMENT_REASON = 'Auto-adjusted shelf quantity to match actual on-hand quantity.';
const PACKAGE_FAMILY_PACKAGING_REASON = 'Package Family';

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    bomCheckButton: {
        minWidth: 'auto',
        padding: '2px 8px',
        margin: 0,
        height: '24px',
        lineHeight: '24px',
    },
    bomCheckComplete: {
        color: '#4CAF50',
        verticalAlign: 'middle',
    },
    bomCheckLink: {
        color: '#2196F3',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bold'
    },
    bomCheckLinkUncompleted: {
        color: '#f44336', // Material UI red
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bold'
    },
    bomCheckLinkCompleted: {
        color: '#4CAF50', // Material UI green
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bold'
    },
    printIcon: {
        cursor: 'pointer',
        color: '#2196F3',
    }
});

class PackageFamily extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        orders: [],
        reloads: [],
        transactions: [],
        calculatorUnpacked: 0,
        page: 0,
        rowsPerPage: 100,
        rebalanceInventory: true,
        bomCheckModalOpen: false,
        selectedAssemblyId: null,
        bomCheckedAssemblies: new Set(),
        overriddenQuantities: {},
        calculatedQuantities: {}, // Store calculated quantities for each assembly
        submissionInProgress: false,
        calculatorSkipped: false,
        packagingComplete: false,
        recentlyProcessedAssemblies: []
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        // Recalculate quantities when relevant state changes
        if (prevState.calculatorUnpacked !== this.state.calculatorUnpacked ||
            prevState.rebalanceInventory !== this.state.rebalanceInventory ||
            prevState.calculatorSkipped !== this.state.calculatorSkipped ||
            prevState.assemblyFamilyId !== this.state.assemblyFamilyId) {
            this.calculateQuantities();
        }
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Workshop')) {
            globalProps.history.push('/login');
            return;
        }

        self.setState({loading: true});
        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts(), self.loadPackageLogs(), self.loadAssemblyFamilies()]).then(() => {
            const assemblyFamilyId = globalProps.match.params.assemblyFamilyId;
            if (assemblyFamilyId && assemblyFamilyId !== '') {
                const assemblyFamily = assemblyFamilies.find(x => x.id === assemblyFamilyId);
                self.setState({
                    assemblies,
                    subAssemblies,
                    assembliesAndSubAssemblies: assemblies.concat(subAssemblies),
                    loading: false,
                    user,
                    packageLogs,
                    permissions: user.userPermissions,
                    assemblyFamily,
                    assemblyFamilyId: assemblyFamily.id
                });
            } else {
                self.setState({
                    assemblies,
                    subAssemblies,
                    assembliesAndSubAssemblies: assemblies.concat(subAssemblies),
                    loading: false,
                    user,
                    packageLogs,
                    permissions: user.userPermissions
                });
            }
        });
    };

    loadAssemblies = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assembliesResult) => {
            assembliesResult.forEach((assembly) => {
                assembly.quantityOnShelf = assembly.totalAssembledOnHandWithRev;
                assembly.minAhead = 2;
                assembly.tgtAhead = 10
            });
            assemblies = assembliesResult.filter(x => x.assembly.isActive).sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                return 0;
            });
        });
    };

    loadSubAssemblies = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssembliesResult) => {
            subAssembliesResult.forEach((assembly) => {
                assembly.minAhead = 2;
                assembly.tgtAhead = 10
            });
            subAssemblies = subAssembliesResult.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId < b.part.partId) return -1;
                if (a.part.partId > b.part.partId) return 1;
                return 0;
            });
            self.setState({parts});
        });
    };

    loadPackageLogs = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getPackageLogs().then((packageLogsResult) => {
            packageLogs = packageLogsResult.sort(function (a, b) {
                return new Date(b.logDate) - new Date(a.logDate);
            });
        });
    };

    loadAssemblyFamilies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAssemblyFamilies().then((assemblyFamiliesResult) => {
            assemblyFamilies = assemblyFamiliesResult;
            self.setState({assemblyFamilies});
        });
    };

    handleAssemblyFamilyChange = (event, newValue) => {
        if (!newValue) this.setState({assemblyFamily: null});
        else {
            const assemblyFamily = this.state.assemblyFamilies.find(x => x.id === newValue.value);
            this.setState({assemblyFamily, assemblyFamilyId: newValue.value});
        }
    };

    roundWithPointSix = (number) => {
        const decimalValue = number - Math.floor(number);
        if (decimalValue < 0.6) return Math.floor(number);
        return Math.round(number);
    }

    formatWeight = (assembly) => {
        const pounds = assembly.weightPounds || 0;
        const ounces = assembly.weightOunces || 0;
        
        if (pounds === 0 && ounces === 0) return "";
        if (pounds === 0) return `${ounces} oz`;
        if (ounces === 0) return `${pounds} lbs`;
        return `${pounds} ${pounds === 1 ? 'lb' : 'lbs'} ${ounces} oz`;
    };

    handleOpenBomCheck = (assemblyId) => {
        this.setState({
            bomCheckModalOpen: true,
            selectedAssemblyId: assemblyId
        });
    };

    handleCloseBomCheck = () => {
        this.setState({
            bomCheckModalOpen: false,
            selectedAssemblyId: null
        });
    };

    handleBomCheckComplete = (assemblyId) => {
        const bomCheckedAssemblies = new Set(this.state.bomCheckedAssemblies);
        bomCheckedAssemblies.add(assemblyId);
        this.setState({ bomCheckedAssemblies });
        this.handleCloseBomCheck();
    };

    // Calculate quantities for all assemblies in the family
    calculateQuantities = () => {
        if (!this.state.assemblyFamilyId || this.state.calculatorSkipped) {
            return;
        }

        const familyAssemblies = this.state.assemblies.filter(
            x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive
        );
        
        const totalSalesInFamily = familyAssemblies.reduce((sum, assembly) => 
            sum + assembly.twentyFourWeekSalesPerWeek, 0);
        const totalQuantityOnShelf = familyAssemblies.reduce((sum, assembly) => 
            sum + assembly.quantityOnShelf, 0);
        const unpackedQty = parseNumberInputValue(this.state.calculatorUnpacked);
        
        // Sort assemblies by sales (highest first)
        const familyAssembliesSorted = [...familyAssemblies].sort(
            (a, b) => b.twentyFourWeekSalesPerWeek - a.twentyFourWeekSalesPerWeek
        );
        
        // First pass: Calculate total rebalancing available (only when rebalancing is enabled)
        let totalRebalancing = 0;
        if (this.state.rebalanceInventory) {
            familyAssembliesSorted.forEach(assembly => {
                const pct = totalSalesInFamily > 0 ? 
                    assembly.twentyFourWeekSalesPerWeek / totalSalesInFamily : 
                    1 / familyAssemblies.length;
                const desired = (totalQuantityOnShelf + unpackedQty) * pct;
                const qtyToPack = desired - assembly.quantityOnShelf;
                
                if (qtyToPack < 0) {
                    const unpackQty = Math.round(Math.abs(qtyToPack)) * -1;
                    totalRebalancing += Math.abs(unpackQty);
                }
            });
        }
        
        // Calculate available inventory to redistribute
        let remainingUnpacked = this.state.rebalanceInventory ? (unpackedQty + totalRebalancing) : unpackedQty;
        const calculatedQuantities = {};
        
        // Second pass: Calculate quantities for each assembly
        familyAssembliesSorted.forEach((assemblyExpanded, index) => {
            const assembly = assemblyExpanded.assembly;
            const pct = totalSalesInFamily > 0 ? 
                assemblyExpanded.twentyFourWeekSalesPerWeek / totalSalesInFamily : 
                1 / familyAssemblies.length;
            const desired = (totalQuantityOnShelf + unpackedQty) * pct;
            let qtyToPack = desired - assemblyExpanded.quantityOnShelf;
            
            let quantityToPack = 0;
            
            if (qtyToPack > 0) {
                if (this.state.rebalanceInventory) {
                    // When rebalancing, use the raw quantity but don't exceed remaining unpacked
                    let roundedQty = this.roundWithPointSix(qtyToPack);
                    quantityToPack = Math.min(roundedQty, remainingUnpacked);
                    remainingUnpacked -= quantityToPack;
                } else {
                    // When not rebalancing, use the raw quantity but don't exceed remaining unpacked
                    let roundedQty = this.roundWithPointSix(qtyToPack);
                    quantityToPack = Math.min(roundedQty, remainingUnpacked);
                    remainingUnpacked -= quantityToPack;
                }
            } else if (this.state.rebalanceInventory && qtyToPack < 0) {
                // Handle unpacking when rebalancing
                quantityToPack = Math.round(Math.abs(qtyToPack)) * -1;
            }
            
            calculatedQuantities[assembly.id] = quantityToPack;
        });
        
        // No adjustment needed - use calculated quantities as-is
        
        this.setState({ calculatedQuantities });
    }

    handleQtyOverride = (assemblyId, value) => {
        const overriddenQuantities = { ...this.state.overriddenQuantities };
        
        if (value === '' || value === null) {
            // Store empty string to indicate user wants to clear the override
            overriddenQuantities[assemblyId] = '';
        } else {
            // Allow partial inputs like "-", "-5", "-50" while typing
            // Check if it's a valid number pattern (including partial inputs)
            const validPattern = /^-?(\d*\.?\d*|\.?\d+)$/;
            if (validPattern.test(value)) {
                // Store the raw string value while typing
                overriddenQuantities[assemblyId] = value;
            }
        }
        
        this.setState({ overriddenQuantities });
    }

    // Get the final quantity to pack for an assembly, considering overrides
    getFinalQuantityToPack = (assembly) => {
        const assemblyId = assembly.id;
        if (assemblyId in this.state.overriddenQuantities) {
            const overrideValue = this.state.overriddenQuantities[assemblyId];
            // If it's empty string, show empty string
            if (overrideValue === '') {
                return '';
            }
            // If it's a string (like "-50"), return it as-is for display
            if (typeof overrideValue === 'string') {
                return overrideValue;
            }
            // If it's explicitly 0, show 0
            if (overrideValue === 0) {
                return 0;
            }
            // For other numeric values, return as-is
            return overrideValue;
        }
        if (assemblyId in this.state.calculatedQuantities) {
            const calculatedValue = this.state.calculatedQuantities[assemblyId];
            // For calculated values, show the actual value (including negative values)
            return calculatedValue;
        }
        return '';
    }

    // Adjust quantities to ensure sum equals target quantity
    adjustQuantitiesToMatchTarget = (quantities, targetQuantity) => {
        if (!quantities || quantities.length === 0) return quantities;
        
        // Calculate current sum
        const currentSum = quantities.reduce((sum, qty) => sum + qty, 0);
        const difference = targetQuantity - currentSum;
        
        // If no difference, no adjustment needed
        if (Math.abs(difference) < 0.01) return quantities;
        
        // Find the largest quantity (or first if multiple have the same largest value)
        let maxIndex = 0;
        let maxValue = quantities[0];
        
        for (let i = 1; i < quantities.length; i++) {
            if (quantities[i] > maxValue) {
                maxValue = quantities[i];
                maxIndex = i;
            }
        }
        
        // Adjust the largest quantity
        quantities[maxIndex] += difference;
        
        // Ensure the adjusted quantity doesn't go negative
        if (quantities[maxIndex] < 0) {
            quantities[maxIndex] = 0;
        }
        
        return quantities;
    }

    // Check if any assemblies with non-zero pack quantities are missing BOM checks
    hasMissingBomChecks = (assembliesToProcess) => {
        return assembliesToProcess.some(item => {
            return item.quantityToPack > 0 && !this.state.bomCheckedAssemblies.has(item.assemblyId);
        });
    }

    handleFinishPackaging = () => {
        // Prevent double submission
        if (this.state.submissionInProgress) return;
        this.setState({ submissionInProgress: true });
        
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const filteredAssemblies = this.state.assemblies.filter(
            x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive
        );
        
        // Sort assemblies by family order for consistent processing
        const assembliesToDisplay = filteredAssemblies.sort(function (a, b) {
            return a.assembly.assemblyFamilyOrder - b.assembly.assemblyFamilyOrder
        });
        
        // Prepare data for each assembly using the stored state values
        const assembliesToProcess = assembliesToDisplay.map((assemblyExpanded) => {
            const assembly = assemblyExpanded.assembly;
            
            // Get the quantity from state (override takes precedence, then calculated, then 0)
            const quantityToPack = this.getFinalQuantityToPack(assembly);
            
            return {
                assemblyId: assembly.id,
                quantityToPack: quantityToPack,
                assemblyName: assembly.assemblyId,
                currentShelfQty: assemblyExpanded.quantityOnShelf,
                actualOnHandQty: assemblyExpanded.totalAssembledOnHandWithRev,
                bomChecked: this.state.bomCheckedAssemblies.has(assembly.id)
            };
        }).filter(item => item.quantityToPack !== 0 || item.currentShelfQty !== item.actualOnHandQty);
        
        // If no items to process, show alert and exit
        if (assembliesToProcess.length === 0) {
            window.$alertWarning("No items to package or adjust.");
            this.setState({ submissionInProgress: false });
            return;
        }
        
        // Check if any assemblies with quantity to pack > 0 are missing BOM checks
        const hasMissingChecks = this.hasMissingBomChecks(assembliesToProcess);
        
        if (hasMissingChecks) {
            // Ask for confirmation
            if (!window.confirm("Some assemblies are missing BOM checks. Are you sure you want to continue?")) {
                this.setState({ submissionInProgress: false });
                return;
            }
        }
        
        // Create inventory adjustments and package logs (adjustments first, then packaging)
        const adjustmentEntries = [];
        const packagingEntries = [];

        for (const item of assembliesToProcess) {
            if (item.currentShelfQty !== item.actualOnHandQty) {
                const confirm = window.confirm(
                    "Adjust shelf quantity to match actual on-hand quantity for " + item.assemblyName + "?"
                );
                if (confirm) {
                    const adjustmentDelta = item.currentShelfQty - item.actualOnHandQty;
                    if (adjustmentDelta !== 0) {
                        adjustmentEntries.push({
                            assemblyId: item.assemblyId,
                            quantity: adjustmentDelta,
                        });
                    }
                }
            }

            const quantityToPack = parseNumberInputValue(item.quantityToPack);
            if (quantityToPack !== 0) {
                packagingEntries.push({
                    assemblyId: item.assemblyId,
                    quantity: quantityToPack,
                    bomChecked: item.bomChecked,
                });
            }
        }

        const familyLabel = this.state.assemblyFamily?.assemblyFamilyId;
        const adjustmentReason = familyLabel
            ? `${PACKAGE_FAMILY_ADJUSTMENT_REASON} (${familyLabel})`
            : PACKAGE_FAMILY_ADJUSTMENT_REASON;
        const packagingReason = familyLabel
            ? `${PACKAGE_FAMILY_PACKAGING_REASON} (${familyLabel})`
            : PACKAGE_FAMILY_PACKAGING_REASON;

        const baseMs = Date.now();
        let sequence = 0;
        const logPayloads = [
            ...adjustmentEntries.map((entry) => ({
                ...entry,
                logDate: toUtcForStorage(new Date(baseMs + sequence++)),
                bomChecked: false,
                reason: adjustmentReason,
            })),
            ...packagingEntries.map((entry) => ({
                ...entry,
                logDate: toUtcForStorage(new Date(baseMs + sequence++)),
                reason: packagingReason,
            })),
        ];

        Promise.all(
            logPayloads.map((entry) =>
                assemblyService.createPackageLog({
                    id: uuid(),
                    assemblyId: entry.assemblyId,
                    quantity: entry.quantity,
                    logDate: entry.logDate,
                    bomChecked: entry.bomChecked,
                    reason: entry.reason,
                    userId: AuthenticationService.getInstance().getUserId(),
                })
            )
        ).then(() => {
            window.$alertSuccess("Packaging completed successfully!");
            
            // Store assemblies with positive quantities for print labels screen
            const assembliesForLabels = assembliesToProcess
                .filter(item => item.quantityToPack > 0)
                .map(item => ({
                    assemblyId: item.assemblyId,
                    quantity: item.quantityToPack
                }));
                
            // Set state to show the print labels screen
            this.setState({ 
                submissionInProgress: false,
                packagingComplete: true,
                recentlyProcessedAssemblies: assembliesForLabels
            });
        }).catch(error => {
            console.error("Error during packaging:", error);
            window.$alertError("An error occurred during packaging. Please try again.");
            this.setState({ submissionInProgress: false });
        });
    }

    openPrintLabels = (assemblyId, quantity) => {
        const assemblyExpanded = this.state.assemblies.find(x => x.assembly.id === assemblyId);
        if (!assemblyExpanded) {
            window.$alertError("Unable to retrieve assembly information.");
            return;
        }
        
        const assembly = assemblyExpanded.assembly;
        const user = this.state.user;
        
        let containerHtml = '';
        for (let i = 0; i < quantity; i++) {
            if (i === 0) containerHtml += `<div class='top-gutter'></div>`;

            if (i % 2 === 0) containerHtml += `<div class='left-gutter'></div>`;

            const uniqueTag = user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase() + new Date(new Date().setHours(0,0,0,0)).toISOString().slice(0,10).replace(/-/g,"");
            
            // Calculate dynamic font size for description based on length
            const fullDescription = assembly.description || "";
            const descriptionLength = fullDescription.length;
            let fontSize = 13; // Default font size
            
            // Adjust font size based on description length
            if (descriptionLength > 100) {
                fontSize = 8;
            } else if (descriptionLength > 80) {
                fontSize = 9;
            } else if (descriptionLength > 60) {
                fontSize = 10;
            } else if (descriptionLength > 40) {
                fontSize = 11;
            } else if (descriptionLength > 20) {
                fontSize = 12;
            }
            
            // Check if install video URL exists
            const hasInstallVideo = assembly.installVideoURL && assembly.installVideoURL.trim() !== "";
            
            containerHtml += `<div class="label-4x2">
                <div class="rev-tag">${assembly.revision}</div>
                <div class="label-left">
                    <div><img src="https://jxbstorage.blob.core.windows.net/jxbui/JXB%20BW.png" width="160"/></div>
                    <div style="font-weight: 700; font-size: 28px;">${assembly.assemblyId}</div>
                    <div style="font-size: ${fontSize}px; line-height: 1.2; word-break: break-word; overflow-wrap: break-word;">${fullDescription}</div>
                    <div style="font-size: 17px; font-weight: 1000; margin-top: 3px;">${this.formatWeight(assembly)}</div>
                </div>
                <div class="label-right">
                    ${hasInstallVideo ? 
                        `<div style="position: relative;">
                            <img src="https://jxbstorage.blob.core.windows.net/jxbui/Install%20Video.png" width="125"/>
                            <img src='https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${assembly.installVideoURL}' style="position: absolute; left: 21px; top: 29px; width: 100px; height: 100px;"/>
                        </div>` 
                        : ''
                    }
                </div>
                <div class="unique-tag">${uniqueTag}</div>
            </div>`;

            if (i % 2 === 0) containerHtml += `<div class='center-gutter'></div>`;
            else containerHtml += `<div class='right-gutter'></div>`;

            if (i === quantity - 1) containerHtml += `<div class='bottom-gutter'></div>`;
        }

        const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
        win.document.body.innerHTML = `<html><head><style>@media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }.label-left {width: 54%; display: inline-block; font-size: 13px; padding: 0 9px;} .label-right {width: 37%; display: inline-block; font-size: 11px; font-style: italic;} .labels_page {font-family: Montserrat, sans-serif; width: 4in; position: absolute; top: 0; left: 0; .label-4x2 { height: 2in; text-align: center; display: flex; align-items: center; position: relative; } .unique-tag { position: absolute; right: 16px; bottom: 8px; font-size: 10px; font-style: italic; }.rev-tag { position: absolute; width: 14px; height: 14px; left: 13px; bottom: 8px; font-size: 15px; border: 1px solid black; padding: 5px; line-height: 14px; border-radius: 50%; font-weight: 600; }</style></head><body><div id="container" class="labels_page">` + containerHtml + `</div></body></html>`;
        setTimeout(() => {
            win.print();
        }, 2000);
    };

    printAllLabels = () => {
        const user = this.state.user;
        let containerHtml = '';
        let totalLabels = 0;
        
        // Process each assembly and add its labels to the container
        for (const item of this.state.recentlyProcessedAssemblies) {
            const assemblyExpanded = this.state.assemblies.find(x => x.assembly.id === item.assemblyId);
            if (!assemblyExpanded) continue;
            
            const assembly = assemblyExpanded.assembly;
            const quantity = item.quantity;
            
            for (let i = 0; i < quantity; i++) {
                if (totalLabels === 0) containerHtml += `<div class='top-gutter'></div>`;

                if (totalLabels % 2 === 0) containerHtml += `<div class='left-gutter'></div>`;

                const uniqueTag = user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase() + new Date(new Date().setHours(0,0,0,0)).toISOString().slice(0,10).replace(/-/g,"");
                
                // Calculate dynamic font size for description based on length
                const fullDescription = assembly.description || "";
                const descriptionLength = fullDescription.length;
                let fontSize = 13; // Default font size
                
                // Adjust font size based on description length
                if (descriptionLength > 100) {
                    fontSize = 8;
                } else if (descriptionLength > 80) {
                    fontSize = 9;
                } else if (descriptionLength > 60) {
                    fontSize = 10;
                } else if (descriptionLength > 40) {
                    fontSize = 11;
                } else if (descriptionLength > 20) {
                    fontSize = 12;
                }
                
                // Check if install video URL exists
                const hasInstallVideo = assembly.installVideoURL && assembly.installVideoURL.trim() !== "";
                
                containerHtml += `<div class="label-4x2">
                    <div class="rev-tag">${assembly.revision}</div>
                    <div class="label-left">
                        <div><img src="https://jxbstorage.blob.core.windows.net/jxbui/JXB%20BW.png" width="160"/></div>
                        <div style="font-weight: 700; font-size: 28px;">${assembly.assemblyId}</div>
                        <div style="font-size: ${fontSize}px; line-height: 1.2; word-break: break-word; overflow-wrap: break-word;">${fullDescription}</div>
                        <div style="font-size: 17px; font-weight: 1000; margin-top: 3px;">${this.formatWeight(assembly)}</div>
                    </div>
                    <div class="label-right">
                        ${hasInstallVideo ? 
                            `<div style="position: relative;">
                                <img src="https://jxbstorage.blob.core.windows.net/jxbui/Install%20Video.png" width="125"/>
                                <img src='https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${assembly.installVideoURL}' style="position: absolute; left: 21px; top: 29px; width: 100px; height: 100px;"/>
                            </div>` 
                            : ''
                        }
                    </div>
                    <div class="unique-tag">${uniqueTag}</div>
                </div>`;

                if (totalLabels % 2 === 0) containerHtml += `<div class='center-gutter'></div>`;
                else containerHtml += `<div class='right-gutter'></div>`;

                totalLabels++;
            }
        }
        
        if (totalLabels > 0) {
            containerHtml += `<div class='bottom-gutter'></div>`;
            
            const win = window.open("", "Title", "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=no,width=384,height=1100");
            win.document.body.innerHTML = `<html><head><style>@media print { @page { margin: 0 !important; } body, html { margin: 0 !important; padding: 0 !important; } }.label-left {width: 54%; display: inline-block; font-size: 13px; padding: 0 9px;} .label-right {width: 37%; display: inline-block; font-size: 11px; font-style: italic;} .labels_page {font-family: Montserrat, sans-serif; width: 4in; position: absolute; top: 0; left: 0; .label-4x2 { height: 2in; text-align: center; display: flex; align-items: center; position: relative; } .unique-tag { position: absolute; right: 16px; bottom: 8px; font-size: 10px; font-style: italic; }.rev-tag { position: absolute; width: 14px; height: 14px; left: 13px; bottom: 8px; font-size: 15px; border: 1px solid black; padding: 5px; line-height: 14px; border-radius: 50%; font-weight: 600; }</style></head><body><div id="container" class="labels_page">` + containerHtml + `</div></body></html>`;
            setTimeout(() => {
                win.print();
            }, 2000);
        } else {
            window.$alertWarning("No labels to print.");
        }
    };

    handleNewPackaging = () => {
        // Reset state and reload data to start a new packaging process
        this.setState({
            packagingComplete: false,
            recentlyProcessedAssemblies: [],
            overriddenQuantities: {},
            calculatedQuantities: {},
            bomCheckedAssemblies: new Set(),
            calculatorSkipped: false,
            calculatorUnpacked: 0
        });
        this.loadData();
    }

    render() {
        const self = this;
        const { classes } = this.props;
        
        if (this.state.loading) {
            return (
                <WorkshopTemplate>
                    <div className="mobile-container">
                        <CircularProgress/>
                    </div>
                </WorkshopTemplate>
            );
        }

        // If packaging is complete, show the print labels screen
        if (this.state.packagingComplete) {
            return (
                <WorkshopTemplate>
                    <div className="mobile-container">
                        <Button 
                            variant="contained"
                            color="primary"
                            onClick={() => globalProps.history.push('/search')}
                            className="success-button-lg"
                        >
                            Workshop Home
                        </Button>
                        <h1>Packaging Complete</h1>
                        
                        <p>Packaging has been completed successfully. You can now print labels for the packaged assemblies:</p>
                        
                        <div style={{marginBottom: 20}}>
                            <Button 
                                variant="contained"
                                color="primary"
                                onClick={this.printAllLabels} 
                                className="success-button-lg"
                            >
                                <PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Print All Labels
                            </Button>
                        </div>
                        
                        <table className="parts-table" style={{marginTop: 20, textAlign: 'center', minWidth: 0}}>
                            <thead>
                                <tr style={{backgroundColor: "#000", color: "#FFF"}}>
                                    <th style={{minWidth: 125}}>Part Number</th>
                                    <th style={{minWidth: 80}}>Quantity</th>
                                    <th style={{minWidth: 80}}>Print Labels</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.recentlyProcessedAssemblies.map(item => {
                                    const assemblyExpanded = this.state.assemblies.find(x => x.assembly.id === item.assemblyId);
                                    if (!assemblyExpanded) return null;
                                    
                                    const assembly = assemblyExpanded.assembly;
                                    return (
                                        <tr key={assembly.id}>
                                            <td className="text-left">
                                                {assembly.assemblyId} rev {assembly.revision}
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td>
                                                <PrintIcon 
                                                    className={classes.printIcon} 
                                                    onClick={() => this.openPrintLabels(assembly.id, item.quantity)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        
                        <div style={{marginTop: 30}}>
                            <Button 
                                variant="contained"
                                color="primary"
                                onClick={this.handleNewPackaging} 
                                className="success-button-lg"
                            >
                                Package More Assemblies
                            </Button>
                        </div>
                    </div>
                </WorkshopTemplate>
            );
        }

        let assemblyOptions = [];
        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            assemblyOptions.push({
                title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`,
                value: assembly.assembly.id
            });
        });
        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((subAssembly) => {
            assemblyOptions.push({
                title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}`,
                value: subAssembly.subAssembly.id
            });
        });

        let totalSalesInFamily = 0.0;
        let totalQuantityOnShelf = 0.0;
        let totalSalesInFamilyToPack = 0.0;
        let totalQuantityOnShelfToPack = 0.0;
        let totalSimulatedPack = 0.0;
        let assembliesToPack = [];
        if (this.state.assemblyFamilyId) {
            // Get all assemblies in this family
            const familyAssemblies = this.state.assemblies.filter(
                x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive
            );
            
            // Calculate totals
            familyAssemblies.forEach((assemblyExpanded) => {
                totalSalesInFamily += assemblyExpanded.twentyFourWeekSalesPerWeek;
                totalQuantityOnShelf += assemblyExpanded.quantityOnShelf;
            });
            
            // Sort assemblies by sales (highest to lowest)
            const sortedAssemblies = [...familyAssemblies].sort(
                (a, b) => b.twentyFourWeekSalesPerWeek - a.twentyFourWeekSalesPerWeek
            );
            
            const unpackedQty = Number.parseInt(this.state.calculatorUnpacked);

            // First pass: Calculate desired quantities without exceeding the total
            let remainingUnpacked = unpackedQty;
            let totalRebalancing = 0; // Track total quantity that can be redistributed
            
            // First pass: Identify assemblies to unpack (negative quantities)
            sortedAssemblies.forEach(assemblyExpanded => {
                // Calculate percentage of sales
                const percentOfSales = totalSalesInFamily > 0 ? 
                    assemblyExpanded.twentyFourWeekSalesPerWeek / totalSalesInFamily : 
                    1 / familyAssemblies.length;
                
                // Calculate desired shelf quantity
                let desiredShelfQuantity = (totalQuantityOnShelf + unpackedQty) * percentOfSales;
                let rawQuantityToPack = desiredShelfQuantity - assemblyExpanded.quantityOnShelf;
                
                // Check if we should unpack this assembly
                if (this.state.rebalanceInventory && rawQuantityToPack < 0) {
                    // Calculate how much to unpack (negative value)
                    let unpackQty = Math.round(Math.abs(rawQuantityToPack)) * -1;
                    // Track total available for redistribution
                    totalRebalancing += Math.abs(unpackQty);
                }
            });
            
            // Add the rebalancing quantity to the remaining unpacked for redistribution
            remainingUnpacked += totalRebalancing;
            
            // Second pass: Distribute to assemblies that need more
            sortedAssemblies.forEach(assemblyExpanded => {
                // Calculate percentage of sales
                const percentOfSales = totalSalesInFamily > 0 ? 
                    assemblyExpanded.twentyFourWeekSalesPerWeek / totalSalesInFamily : 
                    1 / familyAssemblies.length;
                
                // Calculate desired shelf quantity
                let desiredShelfQuantity = (totalQuantityOnShelf + unpackedQty) * percentOfSales;
                let rawQuantityToPack = desiredShelfQuantity - assemblyExpanded.quantityOnShelf;
                
                // Check if it needs to be included in the calculation
                let includeInPacking = false;
                let processedQty = rawQuantityToPack;
                
                if (rawQuantityToPack > 0) {
                    includeInPacking = true;
                    
                    // Round quantity to pack using standard rule
                    let roundedQtyToPack = this.roundWithPointSix(processedQty);
                    
                    // Don't exceed remaining unpacked quantity (including rebalanced)
                    if (roundedQtyToPack > remainingUnpacked) {
                        roundedQtyToPack = remainingUnpacked;
                    }
                    
                    // Update remaining unpacked and total
                    if (roundedQtyToPack > 0) {
                        remainingUnpacked -= roundedQtyToPack;
                        totalSimulatedPack += roundedQtyToPack;
                    }
                } else if (this.state.rebalanceInventory && rawQuantityToPack < 0) {
                    // Include assemblies to unpack
                    includeInPacking = true;
                    processedQty = Math.round(Math.abs(rawQuantityToPack)) * -1;
                    totalSimulatedPack += processedQty; // Add the negative value directly
                }
                
                if (includeInPacking) {
                    // Add to list of assemblies to pack
                    assembliesToPack.push(assemblyExpanded);
                    totalSalesInFamilyToPack += assemblyExpanded.twentyFourWeekSalesPerWeek;
                    totalQuantityOnShelfToPack += assemblyExpanded.quantityOnShelf;
                }
            });
            
            // Note: We no longer need to store these totals in state since we use calculatedQuantities
        }

        return (
            <WorkshopTemplate>
                <div className="mobile-container">
                    <Button 
                        variant="contained"
                        color="primary"
                        onClick={() => globalProps.history.push('/search')}
                        className="success-button-lg"
                    >
                        Workshop Home
                    </Button>
                    <h1>Package Family</h1>
                    <div>
                        {this.state.assemblyFamily ?
                            <div>
                                <h2>{this.state.assemblyFamily.assemblyFamilyId}</h2>
                                {!this.state.calculatorSkipped ?
                                    <>
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={() => this.setState({calculatorSkipped: true})}
                                            className="success-button-lg"
                                        >
                                            Skip Packaging Calculator
                                        </Button>
                                        <br />
                                        <TextField value={this.state.calculatorUnpacked || ''}
                                                   onChange={(event) => this.setState({calculatorUnpacked: event.target.value})}
                                                   variant="outlined" style={{marginTop: 20}}
                                                   label="Unpacked Quantity"/>
                                        <br/><br/>
                                        <Checkbox onClick={(event) => {
                                            this.setState({rebalanceInventory: event.target.checked})
                                        }} checked={this.state.rebalanceInventory} style={{padding: 0}}/> Rebalance
                                        Inventory
                                    </>
                                    :
                                    <Button 
                                        variant="contained"
                                        color="primary"
                                        onClick={() => this.setState({calculatorSkipped: false})}
                                        className="success-button-lg"
                                    >
                                        Use Packaging Calculator
                                    </Button>
                                }
                                <br/>
                                <table className="parts-table"
                                       style={{marginTop: 20, textAlign: 'center', minWidth: 0}}>
                                    <thead>
                                    <tr style={{backgroundColor: "#000", color: "#FFF"}}>
                                        <th style={{minWidth: window.innerWidth > 960 ? 125 : 50}}>PN</th>
                                        <th style={{minWidth: 40}}>Qty On<br/>Shelf</th>
                                        {!this.state.calculatorSkipped && (
                                            <>
                                                <th style={{minWidth: 65}} className="hidden-mobile">24 week<br/>Sales/Wk</th>
                                                <th style={{minWidth: 65}} className="hidden-mobile">% of Sales</th>
                                                <th style={{minWidth: 65}}>Desired<br/>Shelf Qty</th>
                                            </>
                                        )}
                                        <th style={{minWidth: 40}}>Qty to<br/>Pack</th>
                                        <th style={{minWidth: 80}}>BOM Check</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(() => {
                                        // Get assemblies to display sorted by family order
                                        const assembliesToDisplay = self.state.assemblies.filter(x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive).sort(function (a, b) {return a.assembly.assemblyFamilyOrder - b.assembly.assemblyFamilyOrder});
                                        
                                        // Return the mapped components using stored state values
                                        return assembliesToDisplay.map((assemblyExpanded) => {
                                            const assembly = assemblyExpanded.assembly;
                                            const isBomChecked = this.state.bomCheckedAssemblies.has(assembly.id);

                                        return (
                                            <tr>
                                                <td className="text-left">{assembly.assemblyId} rev {assembly.revision}</td>
                                                                                                 <td><TextField
                                                     value={assemblyExpanded.quantityOnShelf === 0 ? 0 : (assemblyExpanded.quantityOnShelf || '')}
                                                     onChange={(event) => {
                                                         const newValue = event.target.value;
                                                         if (newValue === '' || newValue === null) {
                                                             assemblyExpanded.quantityOnShelf = 0;
                                                         } else {
                                                             assemblyExpanded.quantityOnShelf = parseNumberInputValue(newValue);
                                                         }
                                                         // Trigger recalculation of quantities when qty on shelf changes
                                                         this.calculateQuantities();
                                                         // Always update state to refresh the UI, even in skip calculator mode
                                                         this.setState({updated: new Date()});
                                                     }} variant="outlined"
                                                    style={{
                                                        width: 40,
                                                        margin: '0 auto',
                                                        display: 'block'
                                                    }}/>
                                                </td>
                                                {!this.state.calculatorSkipped && (
                                                    <>
                                                        <td className="hidden-mobile">{assemblyExpanded.twentyFourWeekSalesPerWeek.toFixed(2)}</td>
                                                        <td className="hidden-mobile">{(totalSalesInFamily > 0 ? (assemblyExpanded.twentyFourWeekSalesPerWeek / totalSalesInFamily) * 100 : (100 / this.state.assemblies.filter(x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive).length)).toFixed(0)}%</td>
                                                        <td>{((totalQuantityOnShelf + Number.parseInt(this.state.calculatorUnpacked)) * (totalSalesInFamily > 0 ? assemblyExpanded.twentyFourWeekSalesPerWeek / totalSalesInFamily : 1 / this.state.assemblies.filter(x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive).length)).toFixed(0)}</td>
                                                    </>
                                                )}
                                                <td style={{backgroundColor: this.state.calculatorSkipped ? '#FFFFFF' : '#92D050'}}>
                                                                                                         <TextField
                                                         value={this.getFinalQuantityToPack(assembly)}
                                                         onChange={(event) => this.handleQtyOverride(assembly.id, event.target.value)}
                                                        variant="outlined"
                                                        style={{
                                                            width: 40,
                                                            margin: '0 auto',
                                                            display: 'block'
                                                        }}
                                                        inputProps={{
                                                            style: { 
                                                                textAlign: 'center',
                                                                fontWeight: 'bold'
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    {isBomChecked ? (
                                                        <>
                                                            <span
                                                                className={classes.bomCheckLinkCompleted}
                                                                onClick={() => this.handleOpenBomCheck(assembly.id)}
                                                            >
                                                                BOM Check
                                                            </span>
                                                            <CheckCircleIcon className={classes.bomCheckComplete} style={{ marginLeft: '8px' }} />
                                                        </>
                                                    ) : (
                                                        <span
                                                            className={classes.bomCheckLinkUncompleted}
                                                            onClick={() => this.handleOpenBomCheck(assembly.id)}
                                                        >
                                                            BOM Check
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    });
                                })()}
                                    </tbody>
                                </table>
                            </div>
                            : ""}
                    </div>
                    
                    {this.state.assemblyFamily && (
                        <div style={{ marginTop: 30, textAlign: 'center' }}>
                            {this.state.submissionInProgress ? (
                                <CircularProgress />
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.handleFinishPackaging}
                                        className="success-button-lg"
                                        style={{ marginRight: '16px' }}
                                    >
                                        Finish Packaging
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            const filteredAssemblies = this.state.assemblies
                                                .filter(x => x.assembly.assemblyFamilyId === this.state.assemblyFamilyId && x.assembly.isActive);
                                            
                                            // Prepare assemblies to print using stored state values
                                            const assembliesToPrint = filteredAssemblies.map(assemblyExpanded => {
                                                const assembly = assemblyExpanded.assembly;
                                                const qtyToPack = this.getFinalQuantityToPack(assembly);
                                                
                                                return {
                                                    assemblyId: assembly.id,
                                                    quantity: qtyToPack
                                                };
                                            }).filter(item => item.quantity > 0);
                                            
                                            if (assembliesToPrint.length === 0) {
                                                window.$alertWarning("No assemblies to print labels for.");
                                                return;
                                            }
                                            
                                            this.setState({ recentlyProcessedAssemblies: assembliesToPrint }, () => {
                                                this.printAllLabels();
                                            });
                                        }}
                                        className="success-button-lg"
                                    >
                                        <PrintIcon style={{verticalAlign: 'middle', marginRight: '8px'}} /> Print Labels
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <BomCheckModal
                    open={this.state.bomCheckModalOpen}
                    onClose={this.handleCloseBomCheck}
                    assemblyId={this.state.selectedAssemblyId}
                    onComplete={this.handleBomCheckComplete}
                />
            </WorkshopTemplate>
        );
    }
}

let globalProps;

PackageFamily.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PackageFamily);
